// Data hooks for the Dashboard and Employees tabs. Every hook reads the
// persisted engine snapshot; `useRecompute` is the only way anything changes.
import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import type { AcaYearResult, DashboardData, EmployeeYearResult, MonthlyHoursRow, SnapshotMeta, W2Row } from "./dashboardEmployeesTypes";

async function getJson<T>(url: string): Promise<T> {
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) {
    let msg = `Request failed (${res.status})`;
    try { const b = await res.json(); msg = b?.error || b?.message || msg; } catch { /* ignore */ }
    throw new Error(msg);
  }
  return res.json();
}

export function useAcaDashboard(clientId: number | undefined, taxYear: number) {
  return useQuery<DashboardData>({
    queryKey: ["/api/aca/dashboard", clientId, taxYear],
    queryFn: () => getJson(`/api/aca/dashboard?clientId=${clientId}&taxYear=${taxYear}`),
    enabled: !!clientId,
  });
}

export function useAcaYear(clientId: number | undefined, taxYear: number, detail: "light" | "full" = "light") {
  return useQuery<{ meta: SnapshotMeta; result: AcaYearResult }>({
    queryKey: ["/api/aca/year", clientId, taxYear, detail],
    queryFn: () => getJson(`/api/aca/year?clientId=${clientId}&taxYear=${taxYear}${detail === "full" ? "&detail=full" : ""}`),
    enabled: !!clientId,
  });
}

export function useAcaEmployeeDetail(clientId: number | undefined, taxYear: number, employeeId: number | null) {
  return useQuery<{ meta: SnapshotMeta; employee: EmployeeYearResult; ale: AcaYearResult["ale"] }>({
    queryKey: ["/api/aca/year/employee", clientId, taxYear, employeeId],
    queryFn: () => getJson(`/api/aca/year/employee?clientId=${clientId}&taxYear=${taxYear}&employeeId=${employeeId}`),
    enabled: !!clientId && !!employeeId,
  });
}

export function useMonthlyHours(clientId: number | undefined, taxYear: number) {
  return useQuery<MonthlyHoursRow[]>({
    queryKey: ["/api/aca/employee-monthlies", clientId, taxYear],
    queryFn: () => getJson(`/api/aca/employee-monthlies?clientId=${clientId}&taxYear=${taxYear}`),
    enabled: !!clientId,
  });
}

export function useW2Wages(clientId: number | undefined, taxYear: number) {
  return useQuery<W2Row[]>({
    queryKey: ["/api/aca/w2-wages", clientId, taxYear],
    queryFn: () => getJson(`/api/aca/w2-wages?clientId=${clientId}&taxYear=${taxYear}`),
    enabled: !!clientId,
  });
}

export function useAcaConstants(taxYear: number) {
  return useQuery<any>({
    queryKey: ["/api/aca/constants", taxYear],
    queryFn: () => getJson(`/api/aca/constants?year=${taxYear}`),
  });
}

/** Invalidate everything derived from the snapshot for this client/year. */
export function invalidateAcaYear(clientId: number | undefined, taxYear: number) {
  for (const key of ["/api/aca/dashboard", "/api/aca/year", "/api/aca/year/employee", "/api/aca/validation", "/api/aca/forms", "/api/aca/reporting-years"]) {
    queryClient.invalidateQueries({ queryKey: [key, clientId, taxYear] });
    queryClient.invalidateQueries({ queryKey: [key] });
  }
}

export function useRecompute(clientId: number | undefined, taxYear: number) {
  return useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/aca/year/recompute", { clientId, taxYear });
      return res.json();
    },
    onSuccess: () => invalidateAcaYear(clientId, taxYear),
  });
}

export function useSaveMonthlyHours(clientId: number | undefined, taxYear: number) {
  return useMutation({
    mutationFn: async (rows: Array<{ employeeId: number; month: number; hours: number | null }>) => {
      const res = await apiRequest("POST", "/api/aca/employee-monthlies/batch", { clientId, taxYear, source: "manual", rows });
      const out = await res.json();
      await apiRequest("POST", "/api/aca/year/recompute", { clientId, taxYear });
      return out;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/aca/employee-monthlies", clientId, taxYear] });
      invalidateAcaYear(clientId, taxYear);
    },
  });
}

export function useSaveW2Wages(clientId: number | undefined, taxYear: number) {
  return useMutation({
    mutationFn: async (rows: Array<{ employeeId: number; box1Wages: number | null; isFinal: boolean }>) => {
      const res = await apiRequest("POST", "/api/aca/w2-wages/batch", { clientId, taxYear, rows });
      const out = await res.json();
      await apiRequest("POST", "/api/aca/year/recompute", { clientId, taxYear });
      return out;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/aca/w2-wages", clientId, taxYear] });
      invalidateAcaYear(clientId, taxYear);
    },
  });
}
